import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

export interface AssetPreviewObject {
  image: string
  name: string
  thumbnail: string
  isLoading: boolean
  error: boolean
  httpStatus: number
}

export interface AssetPreviewForAsset {
  isLoading: boolean
  error: boolean
  httpStatus: number
  assetPreview: AssetPreviewObject[]
}

export interface AssetPreviewDataState {
  assetPreview: Record<string, AssetPreviewForAsset>
}

const initialState: AssetPreviewDataState = {
  assetPreview: {}
}

function updateAssetPreview (
  state: AssetPreviewDataState,
  assetId: string,
  assetPreview: Array<Partial<AssetPreviewObject>>
): Record<string, AssetPreviewForAsset> {
  return {
    ...state.assetPreview,
    [assetId]: {
      assetPreview: assetPreview.map((preview) => ({
        image: preview.image ?? '',
        name: preview.name ?? '',
        thumbnail: preview.thumbnail ?? '',
        isLoading: false,
        error: false,
        httpStatus: 200
      })),
      isLoading: false,
      error: false,
      httpStatus: 200
    }
  }
}

function updateErrorAssetPreview (
  state: AssetPreviewDataState,
  assetId: string,
  httpStatus: number,
  name?: string
): Record<string, AssetPreviewForAsset> {
  if (Object.prototype.hasOwnProperty.call(state.assetPreview, assetId)) {
    if (name === undefined) {
      return {
        ...state.assetPreview,
        [assetId]: {
          assetPreview: state.assetPreview[assetId].assetPreview.map((preview) => ({
            ...preview,
            isLoading: false,
            error: true,
            httpStatus
          })),
          isLoading: false,
          error: true,
          httpStatus
        }
      }
    } else {
      return {
        ...state.assetPreview,
        [assetId]: {
          assetPreview: state.assetPreview[assetId].assetPreview.map((preview) =>
            preview.name === name ? { ...preview, isLoading: false, error: true, httpStatus } : preview
          ),
          isLoading: false,
          error: true,
          httpStatus
        }
      }
    }
  } else {
    return {
      ...state.assetPreview,
      [assetId]: {
        assetPreview: [],
        isLoading: false,
        error: true,
        httpStatus
      }
    }
  }
}

function updateSingleAssetPreviewObject (
  state: AssetPreviewDataState,
  assetId: string,
  previewName: string,
  previewUpdate: Partial<AssetPreviewObject>
): Record<string, AssetPreviewForAsset> {
  return {
    ...state.assetPreview,
    [assetId]: {
      assetPreview: state.assetPreview[assetId].assetPreview.map((preview) =>
        preview.name === previewName ? { ...preview, ...previewUpdate } : preview
      ),
      isLoading: false,
      error: false,
      httpStatus: state.assetPreview[assetId].httpStatus
    }
  }
}

function getLoadingStateForAssetPreview (
  state: AssetPreviewDataState,
  assetId: string,
  name?: string
): Record<string, AssetPreviewForAsset> {
  if (Object.prototype.hasOwnProperty.call(state.assetPreview, assetId)) {
    return {
      ...state.assetPreview,
      [assetId]: {
        ...state.assetPreview[assetId],
        assetPreview: state.assetPreview[assetId].assetPreview.map((preview) =>
          preview.name === name ? { ...preview, isLoading: true, error: false } : preview
        )
      }
    }
  } else {
    return {
      ...state.assetPreview,
      [assetId]: {
        assetPreview: [],
        isLoading: true,
        error: false,
        httpStatus: 200
      }
    }
  }
}

const assetPreviewSlice = createSlice({
  name: 'AssetPreviewsSlice',
  initialState,
  reducers: {
    assetPreviewLoadingAction: (state, action: PayloadAction<{ assetId: string, name?: string }>) => {
      return {
        assetPreview: getLoadingStateForAssetPreview(state, action.payload.assetId, action.payload.name)
      }
    },
    assetPreviewUpdateAction: (state, action: PayloadAction<{ assetPreview: Array<Partial<AssetPreviewObject>>, httpStatus: number, assetId: string }>) => {
      return {
        assetPreview: updateAssetPreview(state, action.payload.assetId, action.payload.assetPreview)
      }
    },
    updateSingleAssetPreviewObjectAction: (state, action: PayloadAction<{
      assetId: string
      previewName: string
      previewUpdate: Partial<AssetPreviewObject>
    }>) => {
      return {
        assetPreview: updateSingleAssetPreviewObject(
          state,
          action.payload.assetId,
          action.payload.previewName,
          action.payload.previewUpdate
        )
      }
    },
    assetPreviewErrorAction: (state, action: PayloadAction<{ httpStatus: number, assetId: string, name: string | undefined }>) => {
      return {
        assetPreview: updateErrorAssetPreview(state, action.payload.assetId, action.payload.httpStatus)
      }
    }
  }
})

export const {
  assetPreviewLoadingAction,
  assetPreviewUpdateAction,
  assetPreviewErrorAction,
  updateSingleAssetPreviewObjectAction
} = assetPreviewSlice.actions

export default assetPreviewSlice.reducer
